import { Link } from 'react-router-dom';
import { Card } from './Card';
import { Btn } from './Btn';
import { Pill } from './Pill';
import { Flag } from './Flag';
import { teamNamePl } from '../lib/flags';
import { phaseLabel } from '../lib/phase';
import { pickDisplayScore } from '../lib/score';
import { formatKickoff } from '../lib/date';
import type { Match } from '../api/types';

export interface MatchCardBet {
  home: number;
  away: number;
  points: number | null;
}

/**
 * The large match card used on the calendar (cards view) and the dashboard
 * "Na żywo" section. Single source of truth so both surfaces stay identical.
 */
export function MatchCardLg({
  match,
  myBet,
  fromUrl,
}: {
  match: Match;
  myBet?: MatchCardBet;
  fromUrl: string;
}) {
  const done = match.status === 'FINISHED';
  const live = match.status === 'LIVE';
  const upcoming = match.status === 'UPCOMING';
  const pending = match.status === 'PENDING_DRAW';
  const hit = done && (myBet?.points ?? 0) > 0;
  const homeLabel = match.homePlaceholder ?? teamNamePl(match.homeTeam);
  const awayLabel = match.awayPlaceholder ?? teamNamePl(match.awayTeam);
  const homeFlag = match.homePlaceholder ?? match.homeTeam;
  const awayFlag = match.awayPlaceholder ?? match.awayTeam;
  const homeSub = match.homePlaceholder ? '' : match.homeTeam;
  const awaySub = match.awayPlaceholder ? '' : match.awayTeam;
  return (
    <Card id={`match-${match.id}`} padding={18} style={{ position: 'relative', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {match.matchNumber != null && (
            <Pill color="gray">M{match.matchNumber}</Pill>
          )}
          <Pill color="gray">{phaseLabel(match.phase, match.group)}</Pill>
          {live && (
            <Pill color="live">
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: '#fff',
                  animation: 'pulse 1.5s infinite',
                }}
              />
              LIVE
            </Pill>
          )}
        </div>
        {match.venue && (
          <div style={{ fontSize: 11.5, color: '#5b6578', textAlign: 'right', minWidth: 0 }}>
            {match.venue}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)', alignItems: 'center', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Flag code={homeFlag} size={36} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{homeLabel}</div>
            <div style={{ fontSize: 11, color: '#5b6578' }}>{homeSub}</div>
          </div>
        </div>

        <div style={{ textAlign: 'center', minWidth: 60 }}>
          {(() => {
            if (!(done || live)) {
              return (
                <>
                  <div
                    className="serif num"
                    style={{
                      fontSize: 32,
                      fontWeight: 700,
                      color: 'rgba(15,30,61,.3)',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    –<span style={{ margin: '0 4px' }}>:</span>–
                  </div>
                  <div style={{ fontSize: 11, color: '#5b6578', marginTop: 2 }}>
                    {formatKickoff(match.kickoff)}
                  </div>
                </>
              );
            }
            const ds = pickDisplayScore(match);
            if (!ds) {
              // LIVE without first goal yet — placeholder until next sync tick.
              return (
                <div
                  className="serif num"
                  style={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: 'rgba(15,30,61,.3)',
                    letterSpacing: '-0.02em',
                  }}
                >
                  –<span style={{ margin: '0 4px' }}>:</span>–
                </div>
              );
            }
            return (
              <>
                <div
                  className="serif num"
                  style={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: live ? '#d0463c' : '#0f1e3d',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {ds.mainHome}
                  <span style={{ color: 'rgba(15,30,61,.25)', margin: '0 4px' }}>–</span>
                  {ds.mainAway}
                </div>
                {ds.annotationVerbose && (
                  <div style={{ fontSize: 11, color: '#5b6578', marginTop: 2 }}>
                    {ds.annotationVerbose}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', textAlign: 'right', minWidth: 0 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{awayLabel}</div>
            <div style={{ fontSize: 11, color: '#5b6578' }}>{awaySub}</div>
          </div>
          <Flag code={awayFlag} size={36} />
        </div>
      </div>

      <div
        style={{
          marginTop: 16,
          paddingTop: 14,
          borderTop: '1px dashed rgba(15,30,61,.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 11, color: '#5b6578', letterSpacing: '.06em', fontWeight: 600 }}>
            {myBet ? 'TWÓJ TYP' : pending ? 'OCZEKUJE NA DRUŻYNY' : upcoming ? 'NIEOBSTAWIONY' : 'BRAK TYPU'}
          </div>
          {myBet && (
            <div
              className="mono"
              style={{
                fontSize: 13,
                fontWeight: 700,
                padding: '4px 10px',
                borderRadius: 8,
                background: hit
                  ? 'rgba(31,157,85,.1)'
                  : done
                  ? 'rgba(208,70,60,.1)'
                  : 'rgba(15,30,61,.06)',
                color: hit ? '#0f6b34' : done ? '#8a2a22' : '#0f1e3d',
              }}
            >
              {myBet.home}–{myBet.away}
            </div>
          )}
          {done && myBet && (
            <div
              className="mono"
              style={{
                fontSize: 13,
                fontWeight: 700,
                padding: '4px 10px',
                borderRadius: 8,
                background: hit ? 'rgba(31,157,85,.1)' : 'rgba(208,70,60,.1)',
                color: hit ? '#0f6b34' : '#8a2a22',
              }}
            >
              {hit ? `+${myBet.points} pkt` : '0 pkt'}
            </div>
          )}
        </div>
        {done || live ? (
          <Link to={`/bet/${match.id}`} state={{ from: fromUrl }}>
            <Btn size="sm" variant="ghost">
              Pokaż
            </Btn>
          </Link>
        ) : myBet ? (
          upcoming ? (
            <Link to={`/bet/${match.id}`} state={{ from: fromUrl }}>
              <Btn size="sm" variant="ghost">
                Zmień
              </Btn>
            </Link>
          ) : null
        ) : pending ? (
          <Btn size="sm" variant="ghost" disabled>
            Obstaw
          </Btn>
        ) : upcoming ? (
          <Link to={`/bet/${match.id}`} state={{ from: fromUrl }}>
            <Btn size="sm" variant="accent">
              Obstaw
            </Btn>
          </Link>
        ) : null}
      </div>
    </Card>
  );
}
